
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Cafe() {
  const { t } = useTranslation();

  useEffect(() => {
    // Update page title and meta description
    document.title = `${t('coffee.hero.title')} - Coffee Export and Market | Lions Trade Expert`;
    
    // Add JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": t('coffee.hero.title'),
      "description": t('coffee.hero.subtitle'),
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/cafe`,
      "author": {
        "@type": "Organization",
        "name": "Lions Trade Expert"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Lions Trade Expert",
        "logo": {
          "@type": "ImageObject",
          "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/cafe`
      },
      "about": {
        "@type": "Thing",
        "name": "Brazilian Coffee Export",
        "description": "Information about Brazilian coffee export, arabica, robusta, and global coffee market"
      },
      "keywords": "Brazilian coffee, coffee export, arabica, robusta, Brazil commodities, coffee market",
      "inLanguage": "en",
      "datePublished": "2024-01-15",
      "dateModified": "2024-01-15"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [t]);

  const coffeeTypes = [
    { 
      name: t('coffee.types.arabica.name'), 
      percentage: t('coffee.types.arabica.percentage'), 
      description: t('coffee.types.arabica.description'),
      regions: t('coffee.types.arabica.regions')
    },
    { 
      name: t('coffee.types.robusta.name'), 
      percentage: t('coffee.types.robusta.percentage'), 
      description: t('coffee.types.robusta.description'),
      regions: t('coffee.types.robusta.regions')
    }
  ];

  const states = [
    { name: t('coffee.states.mg.name'), production: t('coffee.states.mg.production'), specialty: t('coffee.states.mg.specialty') },
    { name: t('coffee.states.es.name'), production: t('coffee.states.es.production'), specialty: t('coffee.states.es.specialty') },
    { name: t('coffee.states.sp.name'), production: t('coffee.states.sp.production'), specialty: t('coffee.states.sp.specialty') },
    { name: t('coffee.states.ba.name'), production: t('coffee.states.ba.production'), specialty: t('coffee.states.ba.specialty') }
  ];

  const destinations = [
    { country: 'Estados Unidos', percentage: '18%', flag: '🇺🇸' },
    { country: 'Alemanha', percentage: '15%', flag: '🇩🇪' },
    { country: 'Itália', percentage: '12%', flag: '🇮🇹' },
    { country: 'Japão', percentage: '8%', flag: '🇯🇵' },
    { country: 'Bélgica', percentage: '7%', flag: '🇧🇪' },
    { country: 'França', percentage: '6%', flag: '🇫🇷' }
  ];

  const processSteps = [
    { step: '1', title: t('coffee.process.step1.title'), description: t('coffee.process.step1.description') },
    { step: '2', title: t('coffee.process.step2.title'), description: t('coffee.process.step2.description') },
    { step: '3', title: t('coffee.process.step3.title'), description: t('coffee.process.step3.description') },
    { step: '4', title: t('coffee.process.step4.title'), description: t('coffee.process.step4.description') },
    { step: '5', title: t('coffee.process.step5.title'), description: t('coffee.process.step5.description') }
  ];

  const trends = [
    {
      title: t('coffee.trends.specialty.title'),
      description: t('coffee.trends.specialty.description'),
      icon: 'ri-star-line'
    },
    {
      title: t('coffee.trends.certifications.title'),
      description: t('coffee.trends.certifications.description'),
      icon: 'ri-leaf-line'
    },
    {
      title: t('coffee.trends.sustainability.title'),
      description: t('coffee.trends.sustainability.description'),
      icon: 'ri-recycle-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/f4f8c0349dade548f8df0401f5e44f31.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
              {t('coffee.hero.title')}
            </h1>
            <h2 className="text-4xl md:text-5xl font-light mb-8 text-amber-300">
              {t('coffee.hero.subtitle')}
            </h2>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-2xl"></i>
        </div>
      </section>

      {/* History Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">
              {t('coffee.history.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mb-6"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">{t('coffee.history.title')}</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    O café chegou ao Brasil em 1727 e se tornou a principal commodity de exportação do país 
                    durante séculos. Hoje, o Brasil produz cerca de 40% do café mundial.
                  </p>
                  <p>
                    A cafeicultura brasileira emprega mais de 3,5 milhões de pessoas, desde pequenos 
                    produtores familiares até grandes fazendas tecnificadas.
                  </p>
                  <p>
                    O setor movimenta mais de R$ 20 bilhões anuais em exportações, sendo fundamental 
                    para a economia nacional e regional.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center bg-white rounded-lg p-6 shadow-md">
                  <div className="text-3xl font-bold text-amber-600 mb-2">40%</div>
                  <div className="text-gray-600">{t('coffee.history.worldProduction')}</div>
                </div>
                <div className="text-center bg-white rounded-lg p-6 shadow-md">
                  <div className="text-3xl font-bold text-amber-600 mb-2">3,5M</div>
                  <div className="text-gray-600">{t('coffee.history.jobs')}</div>
                </div>
                <div className="text-center bg-white rounded-lg p-6 shadow-md">
                  <div className="text-3xl font-bold text-amber-600 mb-2">R$ 20bi</div>
                  <div className="text-gray-600">{t('coffee.history.exports')}</div>
                </div>
                <div className="text-center bg-white rounded-lg p-6 shadow-md">
                  <div className="text-3xl font-bold text-amber-600 mb-2">100+</div>
                  <div className="text-gray-600">{t('coffee.history.importers')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">
              {t('coffee.types.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mb-6"></div>
          </div>
          <h2 className="text-3xl font-bold text-center mb-12">{t('coffee.types.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coffeeTypes.map((type, index) => (
              <div key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-8 shadow-md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-amber-800">{type.name}</h3>
                  <span className="text-3xl font-bold text-amber-600">{type.percentage}</span>
                </div>
                <p className="text-gray-700 mb-4">{type.description}</p>
                <div className="text-sm text-gray-600">
                  <strong>Principais regiões:</strong> {type.regions}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* States Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">
              {t('coffee.states.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mb-6"></div>
          </div>
          <h2 className="text-3xl font-bold text-center mb-12">{t('coffee.states.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {states.map((state, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md text-center">
                <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-amber-600">{state.production}</span>
                </div>
                <h3 className="text-lg font-bold mb-2">{state.name}</h3>
                <p className="text-gray-600 text-sm">{state.specialty}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">
              {t('coffee.process.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mb-6"></div>
          </div>
          <h2 className="text-3xl font-bold text-center mb-12">{t('coffee.process.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {processSteps.map((process, index) => (
              <div key={index} className="text-center">
                <div className="bg-white text-amber-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <span className="font-bold">{process.step}</span>
                </div>
                <h3 className="text-lg font-bold mb-2">{process.title}</h3>
                <p className="text-amber-100 text-sm">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Section */}
      <section className="py-24 bg-gradient-to-r from-amber-900 via-amber-800 to-amber-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
              {t('coffee.market.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-orange-400 mx-auto mb-6"></div>
          </div>
          <h2 className="text-3xl font-bold text-center mb-12">{t('coffee.market.title')}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6">{t('coffee.market.destinations')}</h3>
              <div className="space-y-4">
                {destinations.map((dest, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{dest.flag}</span>
                      <span className="font-medium">{dest.country}</span>
                    </div>
                    <span className="font-bold text-amber-600">{dest.percentage}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold mb-6">{t('coffee.market.factors.title')}</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <i className="ri-line-chart-line text-amber-600 text-xl mt-1"></i>
                  <div>
                    <strong>{t('coffee.market.factors.quotes.title')}</strong>
                    <p className="text-gray-600">{t('coffee.market.factors.quotes.description')}</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-thermometer-line text-amber-600 text-xl mt-1"></i>
                  <div>
                    <strong>{t('coffee.market.factors.climate.title')}</strong>
                    <p className="text-gray-600">{t('coffee.market.factors.climate.description')}</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-cup-line text-amber-600 text-xl mt-1"></i>
                  <div>
                    <strong>{t('coffee.market.factors.quality.title')}</strong>
                    <p className="text-gray-600">{t('coffee.market.factors.quality.description')}</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-truck-line text-amber-600 text-xl mt-1"></i>
                  <div>
                    <strong>{t('coffee.market.factors.logistics.title')}</strong>
                    <p className="text-gray-600">{t('coffee.market.factors.logistics.description')}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Trends Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">
              {t('coffee.trends.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mb-6"></div>
          </div>
          <h2 className="text-3xl font-bold text-center mb-12">{t('coffee.trends.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {trends.map((trend, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-md text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${trend.icon} text-amber-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4">{trend.title}</h3>
                <p className="text-gray-600">{trend.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
